import { PagingResultModelOfDocumentArchiveModel, DeleteMultipleByIdsPayload, ActionPayload, DocumentArchiveModel, UpsertNotePayload, DeleteByIdPayload, UpdateDocumentArchivesPayload, DocumentAcknowledgementStatus } from "../../gql-types.generated";
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { DateTime } from 'luxon';

export enum DownloadOperation {
    html="html",
    raw="raw",
    xml="xml",
    ack="ack"
};

export interface FilterOptions {
    filterClient?: string;
    filterClientId?: string;
    filterIO?: string;
    filterDocType?: string;
    filterPartner?: string;
    filterPartnerId?: string;
    filterControlNum?: string;
    filterFunctionalGroupControlNum?: string;
    filterTransactionControlNum?: string;
    filterPONum?: string;
    filterFileNameVal?: string;
    filterAcknowledgmentStatuses?: string[];
    filterCreatedDateFrom?: DateTime;
    filterCreatedDateTo?: DateTime;
    filterWasNotificationSent?: boolean;
    filterIsNotificationRequired?: boolean;
    filterErpOrderNum?: string;
    filterDocNum?: string;
};

interface SliceState {
    error?: Error;
    documentArchivePagingResult?: PagingResultModelOfDocumentArchiveModel;
    documentArchiveList?: DocumentArchiveModel[];
    documentArchiveRequestsInFlight: number;
    deleteDocumentArchivesStatus?: DeleteMultipleByIdsPayload;
    abandonDocumentArchivesStatus?: UpdateDocumentArchivesPayload;
    acknowledgeDocumentArchivesStatus?: UpdateDocumentArchivesPayload;
    resendEmailsStatus?: ActionPayload;
    resendDocumentStatus?: ActionPayload;
    getDocumentHtmlFileStatus?: ActionPayload;
    documentArchiveByIdResult?: DocumentArchiveModel;
    getDocumentAcknowledgementFileStatus?: ActionPayload;
    upsertDocumentArchiveNoteStatus?: UpsertNotePayload;
    deleteDocumentArchiveNoteStatus?: DeleteByIdPayload;
    currentPage: number;
    pageCursors: Record<number, string | undefined>; // Maps page number to cursor
};

const initialState: SliceState = {
    error: undefined,
    documentArchivePagingResult: undefined,
    documentArchiveList: undefined,
    documentArchiveRequestsInFlight: 0,
    deleteDocumentArchivesStatus: undefined,
    abandonDocumentArchivesStatus: undefined,
    acknowledgeDocumentArchivesStatus: undefined,
    resendEmailsStatus: undefined,
    resendDocumentStatus: undefined,
    getDocumentHtmlFileStatus: undefined,
    documentArchiveByIdResult: undefined,
    getDocumentAcknowledgementFileStatus: undefined,
    upsertDocumentArchiveNoteStatus: undefined,
    deleteDocumentArchiveNoteStatus: undefined,
    currentPage: 1,
    pageCursors: {}
};

export const slice = createSlice({
    name: 'documentArchives',
    initialState,
    reducers: {
        fetchError: (state, action: PayloadAction<Error>) => {
            // eslint-disable-next-line no-param-reassign
            state.error = action.payload;
        },

        clearError: state => {
            // eslint-disable-next-line no-param-reassign
            state.error = undefined;
        },

        clearState: state => {
            // eslint-disable-next-line no-param-reassign
            state.error = undefined;
            // eslint-disable-next-line no-param-reassign
            state.documentArchiveList = undefined;
            // eslint-disable-next-line no-param-reassign
            state.documentArchiveRequestsInFlight = 0;
            // eslint-disable-next-line no-param-reassign
            state.documentArchivePagingResult = undefined;
            // eslint-disable-next-line no-param-reassign
            state.deleteDocumentArchivesStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.abandonDocumentArchivesStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.acknowledgeDocumentArchivesStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.resendEmailsStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.resendDocumentStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.documentArchiveByIdResult = undefined;
            // eslint-disable-next-line no-param-reassign
            state.getDocumentHtmlFileStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.getDocumentAcknowledgementFileStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.upsertDocumentArchiveNoteStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.deleteDocumentArchiveNoteStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.currentPage = 1;
            // eslint-disable-next-line no-param-reassign
            state.pageCursors = {};
        },

        clearAllCursors: (state) => {
            // eslint-disable-next-line no-param-reassign
            state.pageCursors = {};
        },

        setCurrentPage: (state, action: PayloadAction<number>) => {
            // eslint-disable-next-line no-param-reassign
            state.currentPage = action.payload;
        },

        setCursorForPage: (state, action: PayloadAction<{page: number, cursor: string | undefined}>) => {
            // eslint-disable-next-line no-param-reassign
            state.pageCursors[action.payload.page] = action.payload.cursor;
        },

        fetchDocumentArchiveListSuccess: (state, action: PayloadAction<PagingResultModelOfDocumentArchiveModel>) => {
            // eslint-disable-next-line no-param-reassign
            state.documentArchivePagingResult = action.payload;
            // For server-side pagination, replace the list instead of accumulating
            // eslint-disable-next-line no-param-reassign
            state.documentArchiveList = action.payload.nodes as DocumentArchiveModel[] || [];
            
            // Cache the cursor for the next page if there is one
            // This cursor will be used to navigate FROM current page TO next page
            if (action.payload.cursor?.nextPage) {
                // eslint-disable-next-line no-param-reassign
                state.pageCursors[state.currentPage + 1] = action.payload.cursor.nextPage;
            }
        },

        incrementDocumentArchiveRequestsInFlight: state => {
            // eslint-disable-next-line no-param-reassign
            state.documentArchiveRequestsInFlight += 1;
        },

        decrementDocumentArchiveRequestsInFlight: state => {
            // eslint-disable-next-line no-param-reassign
            if (state.documentArchiveRequestsInFlight > 0) state.documentArchiveRequestsInFlight -= 1;
        },

        captureDeleteDocumentArchivesStatus: (state, action: PayloadAction<DeleteMultipleByIdsPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.deleteDocumentArchivesStatus = action.payload;
        },

        captureAbandonDocumentArchivesStatus: (state, action: PayloadAction<UpdateDocumentArchivesPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.abandonDocumentArchivesStatus = action.payload;
        },

        captureAcknowledgeDocumentArchivesStatus: (state, action: PayloadAction<UpdateDocumentArchivesPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.acknowledgeDocumentArchivesStatus = action.payload;
        },

        captureResendEmailsStatus: (state, action: PayloadAction<ActionPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.resendEmailsStatus = action.payload;
        },

        captureResendDocumentStatus: (state, action: PayloadAction<ActionPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.resendDocumentStatus = action.payload;
        },

        fetchDocumentArchiveByIdSuccess: (state, action: PayloadAction<DocumentArchiveModel | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.documentArchiveByIdResult = action.payload;
        },

        captureGetDocumentHtmlFileStatus: (state, action: PayloadAction<ActionPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.getDocumentHtmlFileStatus = action.payload;
        },

        captureGetDocumentAcknowledgementFileStatus: (state, action: PayloadAction<ActionPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.getDocumentAcknowledgementFileStatus = action.payload;
        },

        captureUpsertDocumentArchiveNoteStatus: (state, action: PayloadAction<UpsertNotePayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.upsertDocumentArchiveNoteStatus = action.payload;
        },

        captureDeleteDocumentArchiveNoteStatus: (state, action: PayloadAction<DeleteByIdPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.deleteDocumentArchiveNoteStatus = action.payload;
        },        
    },

});

export const selectError = (state: RootState): Error | undefined => state.documentArchives.error;
export const selectDocumentArchiveList = (state: RootState): DocumentArchiveModel[] | undefined => state.documentArchives.documentArchiveList;
export const selectRequestsInFlight = (state: RootState): number => state.documentArchives.documentArchiveRequestsInFlight;
export const selectDocumentArchivePagingResult = (state: RootState): PagingResultModelOfDocumentArchiveModel | undefined => state.documentArchives.documentArchivePagingResult;
export const selectDeleteDocumentArchivesStatus = (state: RootState): DeleteMultipleByIdsPayload | undefined => state.documentArchives.deleteDocumentArchivesStatus;
export const selectAbandonDocumentArchivesStatus = (state: RootState): UpdateDocumentArchivesPayload | undefined => state.documentArchives.abandonDocumentArchivesStatus;
export const selectAcknowledgeDocumentArchivesStatus = (state: RootState): UpdateDocumentArchivesPayload | undefined => state.documentArchives.acknowledgeDocumentArchivesStatus;
export const selectResendEmailsStatus = (state: RootState): ActionPayload | undefined => state.documentArchives.resendEmailsStatus;
export const selectResendDocumentStatus = (state: RootState): ActionPayload | undefined => state.documentArchives.resendDocumentStatus;
export const selectGetDocumentHtmlFileStatus = (state: RootState): ActionPayload | undefined => state.documentArchives.getDocumentHtmlFileStatus;
export const selectDocumentArchiveById = (state: RootState): DocumentArchiveModel | undefined => state.documentArchives.documentArchiveByIdResult;
export const selectGetDocumentAcknowledgementFileStatus = (state: RootState): ActionPayload | undefined => state.documentArchives.getDocumentAcknowledgementFileStatus;
export const selectUpsertDocumentArchiveNoteStatus = (state: RootState): UpsertNotePayload | undefined => state.documentArchives.upsertDocumentArchiveNoteStatus;
export const selectDeleteDocumentArchiveNoteStatus = (state: RootState): DeleteByIdPayload | undefined => state.documentArchives.deleteDocumentArchiveNoteStatus;
export const selectCurrentPage = (state: RootState): number => state.documentArchives.currentPage;
export const selectPageCursors = (state: RootState): Record<number, string | undefined> => state.documentArchives.pageCursors;

export const {
    clearError,
    fetchError,
    clearState,
    clearAllCursors,
    setCurrentPage,
    setCursorForPage,    
    fetchDocumentArchiveListSuccess,
    incrementDocumentArchiveRequestsInFlight,
    decrementDocumentArchiveRequestsInFlight,
    captureDeleteDocumentArchivesStatus,
    captureAbandonDocumentArchivesStatus,
    captureAcknowledgeDocumentArchivesStatus,
    captureResendEmailsStatus,
    captureResendDocumentStatus,
    fetchDocumentArchiveByIdSuccess,
    captureGetDocumentHtmlFileStatus,
    captureGetDocumentAcknowledgementFileStatus,
    captureUpsertDocumentArchiveNoteStatus,
    captureDeleteDocumentArchiveNoteStatus
} = slice.actions;

export default slice.reducer;